import React, {useCallback, useEffect, useState} from 'react';
import {
  Button,
  Card,
  Empty,
  Image,
  Input,
  message,
  Result,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  DownloadOutlined,
  LockOutlined,
} from '@ant-design/icons';
import {useParams} from 'react-router-dom';
import {useTranslation} from '../../hooks/useTranslation';
import {getPreview} from '../../utils/fileIconMap';
import {
  getPublicDirectoryShare,
  getPublicDirectoryShareItems,
  PublicDirectoryShareInfo,
  PublicDirectoryShareItem,
  unlockPublicDirectoryShare,
} from '../../services/directoryShare';
import './index.css';

const {Title, Text} = Typography;

const DirectorySharePage: React.FC = () => {
  const {shareCode = ''} = useParams<{shareCode: string}>();
  const {t} = useTranslation();
  const [info, setInfo] = useState<PublicDirectoryShareInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [password, setPassword] = useState('');
  const [items, setItems] = useState<PublicDirectoryShareItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [fileType, setFileType] = useState('all');

  const applyNoIndexMeta = useCallback(() => {
    document.title = t('directoryShare.publicPageTitle');
    const ensureMeta = (name: string, content: string) => {
      let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!element) {
        element = document.createElement('meta');
        element.name = name;
        document.head.appendChild(element);
      }
      element.content = content;
    };
    ensureMeta('robots', 'noindex,nofollow');
    ensureMeta('referrer', 'no-referrer');
  }, [t]);

  const loadItems = useCallback(async (
    reset: boolean,
    nextKeyword: string = keyword,
    nextFileType: string = fileType,
  ) => {
    if (!shareCode || itemsLoading) return;
    setItemsLoading(true);
    try {
      const data = await getPublicDirectoryShareItems(shareCode, {
        cursor: reset ? null : cursor,
        keyword: nextKeyword,
        fileType: nextFileType,
        pageSize: 50,
      });
      setItems((previous) => reset ? data.items : [...previous, ...data.items]);
      setCursor(data.nextCursor || null);
      setHasMore(Boolean(data.hasMore));
    } catch (error) {
      const apiError = error as Error & {status?: number};
      if (apiError.status === 401 || apiError.status === 404) {
        setInfo({state: apiError.status === 401 ? 'LOCKED' : 'UNAVAILABLE'});
        setItems([]);
      }
      message.error(apiError.message || t('directoryShare.itemsFailed'));
    } finally {
      setItemsLoading(false);
    }
  }, [cursor, fileType, itemsLoading, keyword, shareCode, t]);

  useEffect(() => {
    applyNoIndexMeta();
    document.body.classList.add('directory-share-public-body');
    return () => document.body.classList.remove('directory-share-public-body');
  }, [applyNoIndexMeta]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getPublicDirectoryShare(shareCode)
      .then((data) => {
        if (!active) return;
        setInfo(data);
        if (data.state === 'AVAILABLE') {
          setTimeout(() => loadItems(true), 0);
        }
      })
      .catch((error) => {
        if (!active) return;
        setInfo({state: 'UNAVAILABLE'});
        message.error(error instanceof Error ? error.message : t('directoryShare.loadFailed'));
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareCode]);

  const unlock = async () => {
    if (!password) {
      message.warning(t('directoryShare.passwordRequired'));
      return;
    }
    setUnlocking(true);
    try {
      const nextInfo = await unlockPublicDirectoryShare(shareCode, password);
      setInfo(nextInfo);
      setPassword('');
      await loadItems(true);
    } catch (error) {
      message.error(error instanceof Error ? error.message : t('directoryShare.unlockFailed'));
    } finally {
      setUnlocking(false);
    }
  };

  const search = async (value: string) => {
    setKeyword(value);
    await loadItems(true, value, fileType);
  };

  const changeFileType = async (value: string) => {
    setFileType(value);
    await loadItems(true, keyword, value);
  };

  if (loading || !info) {
    return <div className="directory-share-public"><Skeleton active /></div>;
  }

  if (info.state === 'UNAVAILABLE') {
    return (
      <div className="directory-share-public directory-share-public--centered">
        <Result
          status="404"
          title={t('directoryShare.unavailableTitle')}
          subTitle={t('directoryShare.unavailableDescription')}
        />
      </div>
    );
  }

  if (info.state === 'LOCKED') {
    return (
      <div className="directory-share-public directory-share-public--centered">
        <Card className="directory-share-public__lock-card">
          <Space direction="vertical" size={18} style={{width: '100%'}}>
            <div className="directory-share-public__lock-icon"><LockOutlined /></div>
            <div>
              <Title level={2}>{t('directoryShare.lockedTitle')}</Title>
              <Text type="secondary">{t('directoryShare.lockedDescription')}</Text>
            </div>
            <Input.Password
              size="large"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onPressEnter={unlock}
              placeholder={t('directoryShare.passwordPlaceholder')}
              maxLength={64}
            />
            <Button type="primary" size="large" block loading={unlocking} onClick={unlock}>
              {t('directoryShare.unlock')}
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  const directoryName = info.directoryName === 'default'
    ? t('common.defaultDirectory')
    : info.directoryName;

  return (
    <div className="directory-share-public">
      <header className="directory-share-public__hero">
        <div className="directory-share-public__eyebrow">图床小镇/目录分享</div>
        <div className="directory-share-public__title-row">
          <Title level={1}>{directoryName}</Title>
        </div>
        <Space wrap>
          <Tag color="blue">{t('directoryShare.sharedDirectory')}</Tag>
          <Text>{t('directoryShare.fileCount', {count: info.total || 0})}</Text>
          <Text type="secondary">
            {info.expiresAt
              ? t('directoryShare.expiresAt', {time: new Date(info.expiresAt).toLocaleString()})
              : t('directoryShare.neverExpires')}
          </Text>
        </Space>
      </header>

      <section className="directory-share-public__toolbar">
        <Input.Search
          allowClear
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          onSearch={search}
          placeholder={t('directoryShare.searchPlaceholder')}
        />
        <Select
          value={fileType}
          onChange={changeFileType}
          options={[
            {value: 'all', label: t('directoryShare.fileTypes.all')},
            {value: 'image', label: t('directoryShare.fileTypes.image')},
            {value: 'video', label: t('directoryShare.fileTypes.video')},
            {value: 'audio', label: t('directoryShare.fileTypes.audio')},
            {value: 'pdf', label: t('directoryShare.fileTypes.pdf')},
            {value: 'other', label: t('directoryShare.fileTypes.other')},
          ]}
        />
      </section>

      {items.length === 0 && !itemsLoading ? (
        <Empty description={t('directoryShare.empty')} />
      ) : (
        <div className="directory-share-public__grid">
          {items.map((item, index) => (
            <ShareItemCard key={`${item.fileName}-${item.uploadTime}-${index}`} item={item} t={t} />
          ))}
        </div>
      )}

      {itemsLoading && <div className="directory-share-public__loading"><Skeleton active /></div>}
      {hasMore && !itemsLoading && (
        <div className="directory-share-public__more">
          <Button size="large" onClick={() => loadItems(false)}>{t('directoryShare.loadMore')}</Button>
        </div>
      )}
    </div>
  );
};

interface ShareItemCardProps {
  item: PublicDirectoryShareItem;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const ShareItemCard: React.FC<ShareItemCardProps> = ({item, t}) => {
  const preview = (() => {
    if (item.fileCategory === 'image') {
      return <Image src={item.previewUrl} alt={item.fileName} loading="lazy" />;
    }
    if (item.fileCategory === 'video') {
      return <video controls preload="metadata" src={item.previewUrl} />;
    }
    if (item.fileCategory === 'audio') {
      return <audio controls preload="metadata" src={item.previewUrl} />;
    }
    return (
      <div className="directory-share-public__type-icon-frame">
        <img
          className="directory-share-public__type-icon"
          src={getPreview(item.fileName)}
          alt=""
          aria-hidden="true"
          loading="lazy"
          draggable={false}
        />
      </div>
    );
  })();

  return (
    <Card className="directory-share-public__item" cover={<div className="directory-share-public__preview">{preview}</div>}>
      <Space direction="vertical" size={8} style={{width: '100%'}}>
        <Text strong ellipsis={{tooltip: item.fileName}}>{item.fileName}</Text>
        <Space wrap size={6}>
          <Tag>{t(`directoryShare.fileTypes.${item.fileCategory}`)}</Tag>
          <Text type="secondary">{item.size || formatSize(item.sizeBytes)}</Text>
        </Space>
        <Text type="secondary">{new Date(item.uploadTime).toLocaleString()}</Text>
        <Button
          type="primary"
          ghost
          icon={<DownloadOutlined />}
          href={item.downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          referrerPolicy="no-referrer"
          block
        >
          {t('directoryShare.openOrDownload')}
        </Button>
      </Space>
    </Card>
  );
};

const formatSize = (bytes?: number) => {
  if (!bytes || bytes <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

export default DirectorySharePage;
